import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { checkin } from '../services/api';
import useAuthStore from '../store/authStore';
import AuthModal from './AuthModal';

/**
 * CheckinPage
 * Handles QR code check-in flow.
 * URL: /#/checkin?token=xxx
 */
export default function CheckinPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [status, setStatus] = useState('loading'); // loading | success | error | no-token | unauthenticated
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const attemptedRef = useRef(false);

  const handleLoginClick = () => {
    sessionStorage.setItem('larynx_checkin_redirect', window.location.href);
    setAuthModalOpen(true);
  };

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }
    if (!isAuthenticated) {
      setStatus('unauthenticated');
      attemptedRef.current = false;
      return;
    }
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    performCheckin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAuthenticated]);

  const performCheckin = async () => {
    setStatus('loading');
    try {
      const res = await checkin(token);
      setResult(res);
      setStatus('success');
    } catch (err) {
      setErrorMessage(err.message || 'Check-in failed');
      setErrorCode(err.code || '');
      setStatus('error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const cardStyle = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-accent)',
    borderRadius: '1rem',
    padding: '2rem',
    textAlign: 'center',
    maxWidth: '28rem',
    width: '100%',
  };

  return (
    <>
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: 'var(--bg-canvas)' }}
      >
        <div style={{ maxWidth: '28rem', width: '100%' }}>

          {/* Loading */}
          {status === 'loading' && (
            <div style={cardStyle}>
              <div
                className="mx-auto mb-5 rounded-full"
                style={{
                  width: 40, height: 40,
                  border: '3px solid var(--accent-dim)',
                  borderTopColor: 'var(--accent)',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Marking your attendance…
              </p>
            </div>
          )}

          {/* No token */}
          {status === 'no-token' && (
            <div style={cardStyle}>
              <p className="text-4xl mb-3">📷</p>
              <h2
                className="font-display text-xl font-semibold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                No check-in code
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                Scan the QR code shown at the meeting. Don't skip it — that's how you get credit for showing up.
              </p>
              <Link to="/" className="btn-bouncy text-sm px-5 py-2.5">
                Back to home
              </Link>
            </div>
          )}

          {/* Not authenticated */}
          {status === 'unauthenticated' && (
            <div style={cardStyle}>
              <p className="text-4xl mb-3">🔑</p>
              <h2
                className="font-display text-xl font-semibold mb-2"
                style={{ color: 'var(--text-primary)' }}
              >
                Sign in first
              </h2>
              <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
                You need to be signed in to mark your attendance.
              </p>
              <button onClick={handleLoginClick} className="btn-bouncy text-sm px-6 py-2.5">
                Sign in with Zoho
              </button>
            </div>
          )}

          {/* Success */}
          {status === 'success' && result && (
            <div
              style={{
                ...cardStyle,
                border: '1px solid var(--color-success-border)',
              }}
            >
              <p className="text-5xl mb-3">✅</p>
              <h2
                className="font-display text-xl font-semibold mb-2"
                style={{ color: 'var(--color-success)' }}
              >
                You're in.
              </h2>
              {result.meeting && (
                <div className="mb-4">
                  <p className="font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                    {result.meeting.title || 'Weekly Meeting'}
                  </p>
                  <p className="text-sm mt-0.5" style={{ color: 'var(--text-faint)' }}>
                    {formatDate(result.meeting.meeting_date)}
                  </p>
                </div>
              )}
              {result.pointsAwarded && result.pointsEarned > 0 && (
                <div
                  className="rounded-xl p-4 mb-5"
                  style={{
                    background: 'var(--color-info-dim)',
                    border: '1px solid var(--color-info-border)',
                  }}
                >
                  <p className="font-black text-lg font-mono" style={{ color: 'var(--color-info)' }}>
                    +{result.pointsEarned} points
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-info)' }}>
                    For showing up
                  </p>
                </div>
              )}
              <Link to="/" className="btn-bouncy text-sm px-5 py-2.5">
                Back to home
              </Link>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div
              style={{
                ...cardStyle,
                border: `1px solid ${errorCode === 'ALREADY_CHECKED_IN' ? 'var(--color-warning-border)' : 'var(--color-danger-border)'}`,
              }}
            >
              <p className="text-4xl mb-3">
                {errorCode === 'ALREADY_CHECKED_IN' ? '👋' : '❌'}
              </p>
              <h2
                className="font-display text-xl font-semibold mb-2"
                style={{
                  color: errorCode === 'ALREADY_CHECKED_IN' ? 'var(--accent)' : 'var(--color-danger)',
                }}
              >
                {errorCode === 'ALREADY_CHECKED_IN'
                  ? 'Already marked.'
                  : errorCode === 'TOKEN_EXPIRED'
                    ? 'Code expired'
                    : 'Couldn\'t check you in'}
              </h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
                {errorMessage}
              </p>
              {errorCode === 'TOKEN_EXPIRED' && (
                <p className="text-xs mb-4" style={{ color: 'var(--text-faint)' }}>
                  Ask your admin to generate a new QR code.
                </p>
              )}
              <Link to="/" className="btn-bouncy text-sm px-5 py-2.5">
                Go to Dashboard
              </Link>
            </div>
          )}
        </div>
      </div>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
      />
    </>
  );
}
